import { observable } from 'mobx';
import type { Moment } from 'moment';
import moment from 'moment';

export const SearchTypes = {
  ROUND_TRIP: 'roundTrip',
  ONE_WAY: 'oneWay',
  MULTI_LOCATION: 'multipleLocations',
  HOTEL: 'hotel',
  RENTAL_CAR: 'rentalCar'
} as const;

export type SearchType = typeof SearchTypes[keyof typeof SearchTypes];

export type SearchQueryResponseArgs = {
  id: number;
  origin?: string;
  destination?: string;
  departure_time?: string;
  departure_time_type?: number;
  return_time?: string;
  return_time_type?: number;
  hotel_price?: number;
  smoke?: string;
  breakfast?: string;
  roomnum?: number;
  peoplenum?: number;
  use_yahoo?: boolean;
  origin_address?: string;
  destination_address?: string;
  stay?: string;
  stay_address?: string;
  checkin_date?: string;
  checkout_date?: string;
  search_type?: string;
  user_id?: number;
  draft_id?: number;
  hotel_price_prefecture_id?: number;
  is_deleted?: boolean;
  allow_breakfast_domestic?: boolean;
  allow_breakfast_foreign?: boolean;
};

export type SearchQueryArgs = {
  id: number;
  origin?: string;
  destination?: string;
  departureTime?: Moment;
  departureTimeType?: number;
  returnTime?: Moment;
  returnTimeType?: number;
  hotelPrice?: number;
  smoke?: string;
  breakfast?: string;
  roomnum?: number;
  peoplenum?: number;
  useYahoo?: boolean;
  originAddress?: string;
  destinationAddress?: string;
  stay?: string;
  stayAddress?: string;
  checkinDate?: Moment;
  checkoutDate?: Moment;
  searchType?: string;
  userId?: number;
  draftId?: number;
  hotelPricePrefectureId?: number;
  isDeleted?: boolean;
  allowBreakfastDomestic?: boolean;
  allowBreakfastForeign?: boolean;
};

export const convertSearchQueryResponseToArgs = (response: SearchQueryResponseArgs): SearchQueryArgs => {
  return {
    id: response.id,
    origin: response.origin,
    destination: response.destination,
    departureTime: response.departure_time ? moment(response.departure_time) : undefined,
    departureTimeType: response.departure_time_type,
    returnTime: response.return_time ? moment(response.return_time) : undefined,
    returnTimeType: response.return_time_type,
    hotelPrice: response.hotel_price,
    smoke: response.smoke,
    breakfast: response.breakfast,
    roomnum: response.roomnum,
    peoplenum: response.peoplenum,
    useYahoo: response.use_yahoo,
    originAddress: response.origin_address,
    destinationAddress: response.destination_address,
    stay: response.stay,
    stayAddress: response.stay_address,
    checkinDate: response.checkin_date ? moment(response.checkin_date) : undefined,
    checkoutDate: response.checkout_date ? moment(response.checkout_date) : undefined,
    searchType: response.search_type,
    userId: response.user_id,
    draftId: response.draft_id,
    hotelPricePrefectureId: response.hotel_price_prefecture_id,
    isDeleted: response.is_deleted,
    allowBreakfastDomestic: response.allow_breakfast_domestic,
    allowBreakfastForeign: response.allow_breakfast_foreign
  };
};

export default class SearchQuery {
  @observable id: number;
  @observable origin?: string;
  @observable destination?: string;
  @observable departureTime?: Moment;
  @observable departureTimeType?: number;
  @observable returnTime?: Moment;
  @observable returnTimeType?: number;
  @observable hotelPrice?: number;
  @observable smoke?: string;
  @observable breakfast?: string;
  @observable roomnum?: number;
  @observable peoplenum?: number;
  @observable useYahoo?: boolean;
  @observable originAddress?: string;
  @observable destinationAddress?: string;
  @observable stay?: string;
  @observable stayAddress?: string;
  @observable checkinDate?: Moment;
  @observable checkoutDate?: Moment;
  @observable searchType?: string;
  @observable userId?: number;
  @observable draftId?: number;
  @observable hotelPricePrefectureId?: number;
  @observable isDeleted?: boolean;
  @observable allowBreakfastDomestic?: boolean;
  @observable allowBreakfastForeign?: boolean;

  constructor(args: SearchQueryArgs) {
    this.id = args.id;
    this.origin = args.origin;
    this.destination = args.destination;
    this.departureTime = args.departureTime;
    this.departureTimeType = args.departureTimeType;
    this.returnTime = args.returnTime;
    this.returnTimeType = args.returnTimeType;
    this.hotelPrice = args.hotelPrice;
    this.smoke = args.smoke;
    this.breakfast = args.breakfast;
    this.roomnum = args.roomnum;
    this.peoplenum = args.peoplenum;
    this.useYahoo = args.useYahoo;
    this.originAddress = args.originAddress;
    this.destinationAddress = args.destinationAddress;
    this.stay = args.stay;
    this.stayAddress = args.stayAddress;
    this.checkinDate = args.checkinDate;
    this.checkoutDate = args.checkoutDate;
    this.searchType = args.searchType;
    this.userId = args.userId;
    this.draftId = args.draftId;
    this.hotelPricePrefectureId = args.hotelPricePrefectureId;
    this.isDeleted = args.isDeleted;
    this.allowBreakfastDomestic = args.allowBreakfastDomestic;
    this.allowBreakfastForeign = args.allowBreakfastForeign;
  }
}
